import { lazy } from "react";

// Registry of React tools served at /tools/<slug>.
//
// This is the single source of truth for tools: /tools (ToolsIndex) and the
// /projects index both render from it, so a tool added here shows up in both
// without touching either page.
//
// To add a new tool:
//   1. Drop the component in src/tools/<slug>/index.jsx (default export).
//   2. Add an entry below. `component` MUST use lazy(() => import(...)) so
//      each tool is code-split into its own chunk and doesn't bloat the
//      main portfolio bundle.
//   3. Build + deploy (`./deploy.sh portfolio` from the repo root). Firebase's
//      SPA rewrite already routes /tools/<slug> here — no config changes needed.
//
// `hidden: true` keeps a tool routable at /tools/<slug> but out of every
// index (/tools and /projects). Use it for private tools; the tool itself is
// responsible for its own sign-in gate, and its data for its own rules.
const tools = [
  {
    slug: "income-inequality",
    title: "Whose Wealth Rides the Money Supply",
    description:
      "Money supply vs. wealth concentration (the Cantillon chart), plus real income growth by quintile since 1967.",
    added: "2026-07-09",
    tech: ["React", "Recharts", "FRED data"],
    component: lazy(() => import("./income-inequality")),
  },
  {
    slug: "analyzer",
    title: "The Hit Field",
    description:
      "Little League batted-ball physics: hit probability heat map, field simulator, and player luck report from an in-browser logistic regression.",
    added: "2026-07-11",
    tech: ["React", "Logistic regression", "Canvas"],
    component: lazy(() => import("./analyzer")),
  },
  {
    slug: "channel-surfer",
    title: "Channel Surfer",
    description:
      "Search the iptv-org channel guide (~40k channels, ~17k live streams) by name, country, or category, then open any stream in VLC or copy its link.",
    added: "2026-07-20",
    tech: ["React", "iptv-org", "HLS.js"],
    component: lazy(() => import("./channel-surfer")),
  },
  {
    slug: "mise",
    title: "Mise",
    description:
      "A convergence chart for implementation work: tasks on the left bracket rightward into one outcome. Sign in to keep plans, progress, and layout in Firestore.",
    added: "2026-07-31",
    tech: ["React", "Firebase Auth", "Firestore"],
    component: lazy(() => import("./mise")),
  },
  {
    slug: "draft-night",
    title: "Draft Night",
    description:
      "A live fantasy draft board: strike names as they go and the clock, the branches, and your roster reprice themselves. Sign in to keep several leagues, each with its own settings and draft.",
    added: "2026-09-08",
    tech: ["React", "Firebase Auth", "Firestore"],
    component: lazy(() => import("./draft-night")),
  },
  {
    slug: "sunday-desk",
    title: "Sunday Desk",
    description:
      "The in-season half of the fantasy tools: matchup and live scoring, both rosters, standings, and the waiver wire, read from a private ESPN league.",
    added: "2026-09-10",
    tech: ["React", "Cloud Functions", "ESPN Fantasy API"],
    component: lazy(() => import("./sunday-desk")),
  },
  {
    slug: "meal-planner",
    title: "Family Meal Planner",
    description:
      "Recipes with a thumbs rating from each of the four of us, a Monday to Sunday plan (per-person breakfast and lunch, snacks, dinner, dessert), and a kitchen inventory you can dictate into. Shared across the household.",
    added: "2026-09-22",
    tech: ["React", "Firebase Auth", "Firestore"],
    component: lazy(() => import("./meal-planner")),
  },
  {
    slug: "myreadingbuddy",
    title: "My Reading Buddy",
    description:
      "Photograph a picture book spread by spread and read each one aloud. The kids pick it off the shelf, press play, and it reads itself to them, turning the pages as it goes.",
    added: "2026-09-25",
    tech: ["React", "MediaRecorder", "Firebase Storage", "Firestore"],
    component: lazy(() => import("./myreadingbuddy")),
  },
  {
    slug: "seasonal-box",
    title: "Seasonal Box HQ",
    description:
      "Private admin console for the seasonal home box: agents that research, source, curate, and run operations, with every outside action waiting on an approval.",
    added: "2026-10-02",
    tech: ["React", "Cloud Functions", "Claude API", "Firestore"],
    hidden: true,
    component: lazy(() => import("./seasonal-box")),
  },
];

// What the public indexes list.
export const listedTools = tools.filter((t) => !t.hidden);

export default tools;
